import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useAuth, apiErrorMessage } from '~/lib/auth'
import { AppHeader } from '~/components/app-header'
import { GoogleAuthButton } from '~/components/google-auth-button'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Notice } from '~/components/ui/notice'

export const Route = createFileRoute('/register')({
  head: () => ({
    meta: [
      { title: 'Create account — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: RegisterPage,
})

function RegisterPage() {
  const { user, loading, register, verifyEmail, resendVerification } = useAuth()
  const navigate = useNavigate()

  const [phase, setPhase] = useState<'details' | 'verify'>('details')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!loading && user) navigate({ to: '/dpp' })
  }, [loading, user, navigate])

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    setSubmitting(true)
    try {
      await register({
        email,
        password,
        firstName,
        lastName,
        phone: phone || undefined,
      })
      setPhase('verify')
      setNotice('We sent a verification code to ' + email + '.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create account'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    setSubmitting(true)
    try {
      await verifyEmail(code.trim())
      navigate({ to: '/dpp' })
    } catch (err) {
      setError(apiErrorMessage(err, 'Verification failed'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    setError('')
    setNotice('')
    try {
      await resendVerification(email)
      setNotice('A new code is on its way.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not resend code'))
    }
  }

  return (
    <>
      <AppHeader />
      <main className="mx-auto flex min-h-[calc(100vh-64px)] max-w-[1320px] items-center justify-center px-[var(--gutter)] py-16">
        <div className="bracket-frame relative w-full max-w-[480px] border border-[var(--line)] bg-surface p-8 sm:p-10">
          <span className="bk-bl" aria-hidden="true" />
          <span className="bk-br" aria-hidden="true" />

          <div className="mb-2 font-mono text-[11px] tracking-[0.16em] uppercase text-accent">
            Provenance · Account
          </div>
          <h1 className="mb-8 font-mono text-[28px] font-[500] leading-[1.1] text-ink">
            {phase === 'details' ? 'Create account' : 'Verify your email'}
          </h1>

          {phase === 'details' ? (
            <form className="flex flex-col gap-5" onSubmit={handleRegister}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1 212 555 0100"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error && <Notice>{error}</Notice>}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create account'}
              </Button>

              <GoogleAuthButton onError={setError} />
            </form>
          ) : (
            <form className="flex flex-col gap-5" onSubmit={handleVerify}>
              <p className="text-[14px] leading-[1.6] text-ink-soft">
                Enter the verification code we emailed to{' '}
                <span className="text-ink">{email}</span>.
              </p>
              <div>
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error && <Notice>{error}</Notice>}
              {notice && <Notice tone="success">{notice}</Notice>}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? 'Verifying…' : 'Verify & continue'}
              </Button>

              <button
                type="button"
                onClick={handleResend}
                className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute transition-colors hover:text-accent"
              >
                Resend code
              </button>
            </form>
          )}

          {phase === 'details' && (
            <p className="mt-8 border-t border-[var(--line)] pt-6 text-center font-mono text-[11px] tracking-[0.06em] text-ink-mute">
              Already have an account?{' '}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </main>
    </>
  )
}
